// At least six calendar months, including the longest six-month interval.
export const SESSION_MAX_AGE_SECONDS = 200 * 24 * 60 * 60
export const SESSION_STORAGE_KEY = "alomonshi.session.v1"
export const REFRESH_PATH = "/api/v1/auth/refreshToken"

export const tokenExpiresSoon = (token, now = Date.now(), marginSeconds = 60) => {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
    const payload = JSON.parse(atob(part))
    return !Number.isFinite(payload.exp) || payload.exp * 1000 <= now + marginSeconds * 1000
  } catch { return true }
}

export const createSessionController = ({ read, write, clear, request, lock = fn => fn(), now = Date.now }) => {
  let pending = null
  let generation = 0
  const save = data => {
    if (!data?.token || !data?.refreshToken) throw new Error("Incomplete authentication response")
    generation += 1
    write({ token: data.token, refreshToken: data.refreshToken, role: data.role || [] })
    return data.token
  }
  const logout = () => { generation += 1; clear() }
  const ensure = async ({ force = false, rejectedToken = null } = {}) => {
    const initial = read()
    if (initial?.token && !tokenExpiresSoon(initial.token, now()) && (!force || (rejectedToken && initial.token !== rejectedToken))) return initial.token
    if (!initial?.refreshToken) {
      if (initial?.token && tokenExpiresSoon(initial.token, now(), 0)) logout()
      return null
    }
    if (pending) return pending
    const startGeneration = generation
    pending = lock(async () => {
      const current = read()
      // Another tab may have renewed the session while this one waited for the lock.
      if (!current?.refreshToken || generation !== startGeneration) return null
      if (current.refreshToken !== initial.refreshToken && !tokenExpiresSoon(current.token, now())) return current.token
      try {
        const data = await request(current.refreshToken)
        // Never resurrect a session after explicit logout or a different sign-in.
        if (generation !== startGeneration || read()?.refreshToken !== current.refreshToken) return null
        return save(data)
      } catch (error) {
        const status = Number(error?.statusCode ?? error?.status ?? error?.response?.status)
        // A connection error or 5xx must not destroy a six-month session.
        if ([400, 401, 403].includes(status) && generation === startGeneration && read()?.refreshToken === current.refreshToken) logout()
        throw error
      }
    }).finally(() => { pending = null })
    return pending
  }
  return { save, logout, ensure, read }
}
